import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { PaymentMethod } from '~/constants/addPaymentDialogV2Constants'

interface PaymentFormData {
  amountReceived: string
  amountToCharge: string
  authNumber: string
  checkAmount: string
  checkNumber: string
  terminal: string
}

interface UsePaymentMethodValidationProps {
  formData: PaymentFormData
  remainingBalance: number | undefined
  selectedMethod: PaymentMethod
}

interface ValidationResult {
  getNumericValue: (field: keyof PaymentFormData) => number
  getValidationMessage: (
    fieldName: keyof PaymentFormData,
    value: number,
  ) => string | null
  validateForm: () => boolean
}

export const usePaymentMethodValidation = ({
  selectedMethod,
  formData,
  remainingBalance,
}: UsePaymentMethodValidationProps): ValidationResult => {
  const { t } = useTranslation(['Dialogs'])

  const getNumericValue = useCallback(
    (field: keyof PaymentFormData): number => {
      const value = formData[field]
      if (field === 'terminal') return 0
      return value === '' ? 0 : parseFloat(value) || 0
    },
    [formData],
  )

  const getValidationMessage = useCallback(
    (fieldName: keyof PaymentFormData, value: number): string | null => {
      const remainingBalanceValue = remainingBalance || 0
      const stringValue = formData[fieldName] || ''
      const isEmpty = stringValue.trim() === ''

      const validationRules: Record<string, any> = {
        amountToCharge: {
          [PaymentMethod.CREDIT_DEBIT]: [
            {
              condition: isEmpty || value === 0,
              message: t(
                'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.ENTER_AMOUNT_TO_CHARGE',
              ),
            },
            {
              condition: value > remainingBalanceValue,
              message: t(
                'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.AMOUNT_CANNOT_EXCEED_TOTAL_DUE',
              ),
            },
          ],
          [PaymentMethod.CARECREDIT]: [
            {
              condition: isEmpty || value === 0,
              message: t(
                'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.ENTER_THE_AMOUNT_CHARGED_TO_CARECREDIT',
              ),
            },
            {
              condition: value > remainingBalanceValue,
              message: t(
                'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.AMOUNT_CANNOT_EXCEED_TOTAL_DUE',
              ),
            },
          ],
        },
        amountReceived: [
          {
            condition: isEmpty || value === 0,
            message: t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.ENTER_THE_AMOUNT_RECEIVED_IN_CASH',
            ),
          },
        ],
        checkAmount: [
          {
            condition: isEmpty || value === 0,
            message: t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.ENTER_THE_CHECK_AMOUNT',
            ),
          },
          {
            condition: value > remainingBalanceValue,
            message: t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.AMOUNT_CANNOT_EXCEED_TOTAL_DUE',
            ),
          },
        ],
        checkNumber: [
          {
            condition: isEmpty || value === 0,
            message: t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.ENTER_THE_CHECK_NUMBER',
            ),
          },
        ],
        authNumber: [
          {
            condition: isEmpty || value === 0,
            message: t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.ENTER_AUTHORIZATION_NUMBER',
            ),
          },
        ],
      }

      const fieldRules = validationRules[fieldName]
      if (!fieldRules) return null

      if (fieldName === 'amountToCharge') {
        const methodRules = fieldRules[selectedMethod]
        if (!methodRules) return null

        const firstMatchingRule = methodRules.find(
          (rule: any) => rule.condition,
        )
        return firstMatchingRule?.message || null
      }

      const firstMatchingRule = fieldRules.find((rule: any) => rule.condition)
      return firstMatchingRule?.message || null
    },
    [selectedMethod, formData, remainingBalance, t],
  )

  const validateForm = useCallback((): boolean => {
    const remainingBalanceValue = remainingBalance || 0
    if (remainingBalanceValue <= 0) return false

    const selectedMethodConfig: Record<PaymentMethod, boolean> = {
      [PaymentMethod.CREDIT_DEBIT]:
        getNumericValue('amountToCharge') > 0 &&
        getNumericValue('amountToCharge') <= remainingBalanceValue &&
        formData.terminal.trim() !== '',

      [PaymentMethod.CARECREDIT]:
        getNumericValue('amountToCharge') > 0 &&
        getNumericValue('amountToCharge') <= remainingBalanceValue &&
        getNumericValue('authNumber') > 0,

      [PaymentMethod.CASH]: getNumericValue('amountReceived') > 0,

      [PaymentMethod.CHECK]:
        getNumericValue('checkAmount') > 0 &&
        getNumericValue('checkAmount') <= remainingBalanceValue &&
        getNumericValue('checkNumber') > 0,
    } as const
    return selectedMethodConfig[selectedMethod]
  }, [selectedMethod, formData, remainingBalance, getNumericValue])

  return {
    getValidationMessage,
    validateForm,
    getNumericValue,
  }
}

export default usePaymentMethodValidation
